import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  BookOpen, Scale, ExternalLink, Shield, CheckCircle, AlertTriangle, 
  ArrowRight, Sparkles, GraduationCap, Search, ChevronDown, ChevronUp, FileText
} from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

// All 50 states + DC with verified PDF links where available
const REGULATED_STATES = [
  {
    code: "AK",
    name: "Alaska",
    type: "License",
    authority: "Commissioner of Public Safety",
    requirements: "$15,000 bond, written exam, fingerprints, 21+, Alaska business license required",
    rulesUrl: "https://courts.alaska.gov/rules/docs/civ.pdf",
    authorityUrl: "https://dps.alaska.gov/"
  },
  {
    code: "AZ",
    name: "Arizona",
    type: "Certification",
    authority: "Superior Court",
    requirements: "Written exam, 21+, 1-year residency, renewed every 3 years",
    rulesUrl: "https://rulesforum.azcourts.gov/DesktopModules/ActiveForums/viewer.aspx?portalid=4&moduleid=9811&attachmentid=3261",
    authorityUrl: "https://www.azcourts.gov/"
  },
  {
    code: "CA",
    name: "California",
    type: "Registration",
    authority: "County Clerk/Recorder",
    requirements: "$2,000 bond, county registration, valid statewide",
    rulesUrl: "https://www.courts.ca.gov/rules.htm",
    authorityUrl: "https://www.courts.ca.gov/"
  },
  {
    code: "FL",
    name: "Florida",
    type: "Certification",
    authority: "Judicial Circuit/Sheriff",
    requirements: "$5,000 bond, 16-hour FAPPS course, exam (80%+), background check. Varies by circuit.",
    rulesUrl: "https://www-media.floridabar.org/uploads/2025/12/Civil-Procedure-Rules-01-01-26.pdf",
    authorityUrl: "https://www.flcourts.org/",
    countySpecific: true
  },
  {
    code: "GA",
    name: "Georgia",
    type: "Certification",
    authority: "Sheriffs' Association",
    requirements: "$5,000 bond, 12-hour course, state exam ($250), background check, 3-year renewal",
    rulesUrl: "https://www.gasupreme.us/rules/",
    authorityUrl: "https://www.gseriff.com/"
  },
  {
    code: "KY",
    name: "Kentucky",
    type: "Special Bailiff",
    authority: "County Circuit Court",
    requirements: "Court appointment as 'Special Bailiff', bond varies by county, background checks",
    rulesUrl: "https://kycourts.gov/Courts/Pages/Rules-of-Practice.aspx",
    authorityUrl: "https://courts.ky.gov/",
    countySpecific: true
  },
  {
    code: "MO",
    name: "Missouri",
    type: "License",
    authority: "St. Louis Sheriff",
    requirements: "ST. LOUIS CITY ONLY: 16-hour course, exam, $100,000 E&O insurance, 21+. Rest of state has no requirements.",
    rulesUrl: "https://www.courts.mo.gov/page.jsp?id=46",
    authorityUrl: "https://www.stlouis-mo.gov/",
    countySpecific: true
  },
  {
    code: "MT",
    name: "Montana",
    type: "Registration",
    authority: "Montana State",
    requirements: "$10,000 bond, exam required. Only if serving 10+ processes per year.",
    rulesUrl: "https://archive.legmt.gov/bills/2011/mca/25/20/2012%20court%20rules%20-%20title%2025,%20ch%2020%20(Rules%20of%20Civil%20Procedure).pdf",
    authorityUrl: "https://sosmt.gov/"
  },
  {
    code: "NV",
    name: "Nevada",
    type: "License",
    authority: "PILB",
    requirements: "$200,000 liability insurance, written exam, 2 years experience, 21+",
    rulesUrl: "https://www.leg.state.nv.us/courtrules/nrcp.html",
    authorityUrl: "https://pilb.nv.gov/"
  },
  {
    code: "NY",
    name: "New York",
    type: "License",
    authority: "NYC DCWP",
    requirements: "NYC (5 BOROUGHS) ONLY: $10,000 bond, exam, if serving 5+ processes/year",
    rulesUrl: "https://www.nysenate.gov/legislation/laws/CVP",
    authorityUrl: "https://www.nyc.gov/site/dca/index.page",
    countySpecific: true
  },
  {
    code: "OK",
    name: "Oklahoma",
    type: "License",
    authority: "County Court",
    requirements: "$5,000 bond, $35 county license or $150 statewide license",
    rulesUrl: "https://www.oscn.net/applications/oscn/index.asp?ftdb=STOKST12",
    authorityUrl: "https://www.oscn.net/"
  },
  {
    code: "TX",
    name: "Texas",
    type: "Certification",
    authority: "JBCC",
    requirements: "7-hour JBCC-approved course, background check, $200 fee, 2-year certification",
    rulesUrl: "https://www.txcourts.gov/media/1457525/texas-rules-of-civil-procedure-january-1-2026.pdf",
    authorityUrl: "https://www.txcourts.gov/jbcc/"
  },
  {
    code: "WA",
    name: "Washington",
    type: "Registration",
    authority: "County Auditor",
    requirements: "$10/year county auditor registration, no exam required",
    rulesUrl: "https://www.courts.wa.gov/court_rules/?fa=court_rules.list&group=sup&set=cr",
    authorityUrl: "https://www.courts.wa.gov/"
  },
  {
    code: "TN",
    name: "Tennessee",
    type: "Varies",
    authority: "County-specific",
    requirements: "No statewide requirements. SHELBY COUNTY (Memphis): $15,000 bond, background check.",
    rulesUrl: "https://www.tncourts.gov/courts/rules-civil-procedure/rules/rules-civil-procedure-rules/tennessee-rules-civil-procedure",
    authorityUrl: "https://www.tncourts.gov/",
    countySpecific: true
  }
];

// States with no statewide requirements - with verified PDF links
const UNREGULATED_STATES = [
  { code: "AL", name: "Alabama", rulesUrl: "https://judicial.alabama.gov/library/CivilProcedure" },
  { code: "AR", name: "Arkansas", rulesUrl: "https://opinions.arcourts.gov/ark/cr/en/16712/1/document.do" },
  { code: "CO", name: "Colorado", rulesUrl: "https://www.coloradojudicial.gov/media/12395" },
  { code: "CT", name: "Connecticut", rulesUrl: "https://www.jud.ct.gov/pb.htm" },
  { code: "DE", name: "Delaware", rulesUrl: "https://courts.delaware.gov/superior/pdf/civil_rules_2016.pdf" },
  { code: "DC", name: "District of Columbia", rulesUrl: "https://www.dccourts.gov/sites/default/files/superior-rules/Superior%20Court%20Rules%20of%20Civil%20Procedure_0.pdf" },
  { code: "HI", name: "Hawaii", rulesUrl: "https://www.courts.state.hi.us/wp-content/uploads/2024/09/hrcp_ada.pdf" },
  { code: "ID", name: "Idaho", rulesUrl: "https://isc.idaho.gov/orders/IRCP_Newly_Formatted_Rules_Order_02.16.pdf" },
  { code: "IL", name: "Illinois", rulesUrl: "https://www.illinoiscourts.gov/rules-law/supreme-court-rules/", note: "Cook County PI requirement eliminated Jan 2025" },
  { code: "IN", name: "Indiana", rulesUrl: "https://rules.incourts.gov/pdf/PDF%20-%20Trial/trial.pdf" },
  { code: "IA", name: "Iowa", rulesUrl: "https://www.legis.iowa.gov/docs/ACO/CourtRulesChapter/1.pdf" },
  { code: "KS", name: "Kansas", rulesUrl: "https://kscourts.gov/KSCourts/media/KsCourts/Rules/2024-Rule-Book.pdf" },
  { code: "LA", name: "Louisiana", rulesUrl: "https://www.legis.la.gov/legis/Laws_Toc.aspx?folder=68&level=Parent", note: "Court motion may be required" },
  { code: "ME", name: "Maine", rulesUrl: "https://www.courts.maine.gov/rules/rules-civil.html" },
  { code: "MD", name: "Maryland", rulesUrl: "https://www.courts.state.md.us/rules" },
  { code: "MA", name: "Massachusetts", rulesUrl: "https://www.mass.gov/law-library/massachusetts-rules-of-civil-procedure" },
  { code: "MI", name: "Michigan", rulesUrl: "https://www.courts.michigan.gov/rules-administrative-orders-and-jury-instructions/current-rules-and-jury-instructions/michigan-court-rules/" },
  { code: "MN", name: "Minnesota", rulesUrl: "https://www.revisor.mn.gov/court_rules/rule/cp-toh/" },
  { code: "MS", name: "Mississippi", rulesUrl: "https://courts.ms.gov/research/rules/msrulesofcourt/rules_of_civil_procedure.pdf" },
  { code: "NE", name: "Nebraska", rulesUrl: "https://nebraskajudicial.gov/supreme-court-rules/chapter-6-trial-courts/article-11-nebraska-court-rules-pleading-civil-cases-effective-january-1-2025" },
  { code: "NH", name: "New Hampshire", rulesUrl: "https://www.courts.nh.gov/resources/court-rules" },
  { code: "NJ", name: "New Jersey", rulesUrl: "https://www.njcourts.gov/attorneys/rules-of-court" },
  { code: "NM", name: "New Mexico", rulesUrl: "https://supremecourt.nmcourts.gov/wp-content/uploads/sites/2/2024/02/Rule-1-004-NMRA.pdf" },
  { code: "NC", name: "North Carolina", rulesUrl: "https://www.ncleg.net/enactedlegislation/statutes/pdf/bychapter/chapter_1a.pdf" },
  { code: "ND", name: "North Dakota", rulesUrl: "https://www.ndcourts.gov/legal-resources/rules/ndrcivp" },
  { code: "OH", name: "Ohio", rulesUrl: "https://www.supremecourt.ohio.gov/docs/LegalResources/Rules/civil/CivilProcedure.pdf", note: "Court appointment may be required" },
  { code: "OR", name: "Oregon", rulesUrl: "https://www.oregonlegislature.gov/bills_laws/Pages/ORCP.aspx" },
  { code: "PA", name: "Pennsylvania", rulesUrl: "https://www.pacourts.us/Storage/media/pdfs/20231103/180944-civilrules-ruletext.pdf" },
  { code: "RI", name: "Rhode Island", rulesUrl: "https://www.courts.ri.gov/Courts/superiorcourt/Documents/SuperiorCourtRulesOfCivilProcedure.pdf" },
  { code: "SC", name: "South Carolina", rulesUrl: "https://www.sccourts.org/resources/judicial-community/court-rules/" },
  { code: "SD", name: "South Dakota", rulesUrl: "https://sdlegislature.gov/Statutes/15-6" },
  { code: "UT", name: "Utah", rulesUrl: "https://legacy.utcourts.gov/rules/viewall.php?type=urcp" },
  { code: "VT", name: "Vermont", rulesUrl: "https://legislature.vermont.gov/statutes/chapter/12/001" },
  { code: "VA", name: "Virginia", rulesUrl: "https://ris.dls.virginia.gov/uploads/3VAC5/DIBR/81dd6005369~5s.pdf" },
  { code: "WV", name: "West Virginia", rulesUrl: "https://www.courtswv.gov/sites/default/pubfilesmnt/2025-02/RCP%20Final.pdf" },
  { code: "WI", name: "Wisconsin", rulesUrl: "https://docs.legis.wisconsin.gov/document/statutes/801.pdf" },
  { code: "WY", name: "Wyoming", rulesUrl: "https://www.wyocourts.gov/app/uploads/2025/01/Wyoming-Rules-of-Civil-Procedure-August-2024.pdf" }
];

function ResourcesPage() {
  // Set canonical URL
  useCanonical('https://servio.legal/resources');
  
  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];
  
  var regulatedExpandedState = useState(true);
  var regulatedExpanded = regulatedExpandedState[0];
  var setRegulatedExpanded = regulatedExpandedState[1];
  
  var unregulatedExpandedState = useState(false);
  var unregulatedExpanded = unregulatedExpandedState[0];
  var setUnregulatedExpanded = unregulatedExpandedState[1];

  // Filter states based on search
  var searchLower = search.toLowerCase();
  
  var filteredRegulated = REGULATED_STATES.filter(function(state) {
    return state.code.toLowerCase().includes(searchLower) || 
           state.name.toLowerCase().includes(searchLower);
  });
  
  var filteredUnregulated = UNREGULATED_STATES.filter(function(state) {
    return state.code.toLowerCase().includes(searchLower) || 
           state.name.toLowerCase().includes(searchLower);
  });

  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Helmet>
        <title>Process Server Resources & State Requirements | Servio</title>
        <meta name="description" content="Learn about process serving regulations, licensing requirements, and certification rules for all 50 states. Essential resources for legal professionals and process servers." />
        <link rel="canonical" href="https://servio.legal/resources" />
      </Helmet>
      <Navbar />
      
      {/* Hero Section - Condensed */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
        
        <div className="section-container py-12 lg:py-16 relative z-10">
          <div className="text-center max-w-3xl mx-auto animate-slide-up">
            <div className="inline-flex items-center gap-2 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="h-4 w-4 text-teal-400" />
              <span className="text-slate-300">Learning Center</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight">
              <span className="text-white">Process Server</span>{' '}
              <span className="gradient-text">Resources</span>
            </h1>
            
            <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
              State requirements, licensing info, and getting started with Servio.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Start Cards - Horizontal on desktop */}
      <section className="py-8 relative">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Getting Started */}
            <div className="group bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-5 hover:border-teal-500/50 transition-all">
              <div className="flex items-start gap-3">
                <GraduationCap className="h-5 w-5 text-teal-400 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-white mb-2">Getting Started</h3>
                  <ul className="space-y-1.5 text-sm text-slate-400">
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-teal-400 mt-0.5 flex-shrink-0" />Create free account</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-teal-400 mt-0.5 flex-shrink-0" />Complete profile</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-teal-400 mt-0.5 flex-shrink-0" />Add coverage areas</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-teal-400 mt-0.5 flex-shrink-0" />Subscribe to go live</li>
                  </ul>
                  <Link to="/register" className="inline-flex items-center gap-1.5 mt-3 text-teal-400 text-sm font-medium hover:text-teal-300">
                    Get Started <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Maximize Score */}
            <div className="group bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-cyan-400 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-white mb-2">Maximize Your Score</h3>
                  <ul className="space-y-1.5 text-sm text-slate-400">
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />100% profile completion</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Get verified badges</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Respond within 24h</li>
                    <li className="flex gap-2"><CheckCircle className="h-3.5 w-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />Zero complaints</li>
                  </ul>
                  <Link to="/dashboard" className="inline-flex items-center gap-1.5 mt-3 text-cyan-400 text-sm font-medium hover:text-cyan-300">
                    View Dashboard <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Verification Badges */}
            <div className="group bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-5 hover:border-purple-500/50 transition-all">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-purple-400 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-white mb-2">Verification Badges</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-slate-400">
                    <span><strong className="text-white">L</strong> Licensed</span>
                    <span><strong className="text-white">I</strong> Insured</span>
                    <span><strong className="text-white">B</strong> Bonded</span>
                    <span><strong className="text-white">C</strong> Certified</span>
                    <span><strong className="text-white">R</strong> Registered</span>
                  </div>
                  <Link to="/dashboard?tab=documents" className="inline-flex items-center gap-1.5 mt-3 text-purple-400 text-sm font-medium hover:text-purple-300">
                    Upload Docs <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Requirements Section - Condensed */}
      <section className="py-8 relative" id="state-requirements">
        <div className="section-container">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Scale className="h-5 w-5 text-amber-400" />
              State Requirements
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
          </div>

          {/* Search */}
          <div className="max-w-sm mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="text"
                placeholder="Search states..."
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                className="pl-10 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500 h-9"
                data-testid="state-search-input"
              />
            </div>
          </div>

          {/* Universal Requirements Notice */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6">
            <div className="flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-400">
                <span className="text-amber-400 font-medium">All states:</span> Servers must be 18+ and a disinterested party. Always verify current requirements with your state's courts.
              </p>
            </div>
          </div>

          {/* Regulated States - Collapsible Table */}
          <div className="mb-6">
            <button
              onClick={function() { setRegulatedExpanded(!regulatedExpanded); }}
              className="w-full flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/15 transition-colors"
              data-testid="regulated-states-toggle"
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-400" />
                <span className="text-white font-medium text-sm">
                  States With Requirements ({filteredRegulated.length})
                </span>
              </div>
              {regulatedExpanded ? (
                <ChevronUp className="h-4 w-4 text-amber-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-amber-400" />
              )}
            </button>
            
            {regulatedExpanded && filteredRegulated.length > 0 && (
              <div className="mt-2 border border-slate-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-900/80">
                        <th className="px-3 py-2 text-left text-slate-400 font-medium">State</th>
                        <th className="px-3 py-2 text-left text-slate-400 font-medium hidden sm:table-cell">Type</th>
                        <th className="px-3 py-2 text-left text-slate-400 font-medium">Requirements</th>
                        <th className="px-3 py-2 text-center text-slate-400 font-medium">Links</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredRegulated.map(function(state) {
                        return (
                          <tr key={state.code} className="bg-slate-900/40 hover:bg-slate-900/60 transition-colors">
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">{state.code}</span>
                                <span className="text-slate-300 hidden sm:inline">{state.name}</span>
                                <span className="text-slate-300 sm:hidden">{state.name.split(' ')[0]}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 hidden sm:table-cell">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 whitespace-nowrap">
                                  {state.type}
                                </span>
                                {state.countySpecific && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 whitespace-nowrap">
                                    Varies
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              <p className="text-slate-400 text-xs leading-relaxed max-w-md">{state.requirements}</p>
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-center gap-1.5">
                                <a
                                  href={state.rulesUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 rounded bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 transition-colors"
                                  title="Rules of Civil Procedure"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                </a>
                                {state.authorityUrl && (
                                  <a
                                    href={state.authorityUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
                                    title="Licensing Authority"
                                  >
                                    <Shield className="h-3.5 w-3.5" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Unregulated States - Collapsible Compact Grid */}
          <div className="mb-6">
            <button
              onClick={function() { setUnregulatedExpanded(!unregulatedExpanded); }}
              className="w-full flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/15 transition-colors"
              data-testid="unregulated-states-toggle"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                <span className="text-white font-medium text-sm">
                  States Without Statewide Requirements ({filteredUnregulated.length})
                </span>
              </div>
              {unregulatedExpanded ? (
                <ChevronUp className="h-4 w-4 text-emerald-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-emerald-400" />
              )}
            </button>
            
            {unregulatedExpanded && filteredUnregulated.length > 0 && (
              <div className="mt-2 p-3 border border-slate-800 rounded-lg bg-slate-900/40">
                <p className="text-xs text-slate-500 mb-3">
                  18+ and disinterested party only. Local county rules may apply. Click any state to view their Rules of Civil Procedure.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {filteredUnregulated.map(function(state) {
                    return (
                      <a
                        key={state.code}
                        href={state.rulesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-2.5 py-1.5 bg-slate-800/50 rounded hover:bg-slate-800 transition-colors group"
                        title={state.note ? state.name + ': ' + state.note : state.name + ' Rules of Civil Procedure'}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-bold text-white text-xs">{state.code}</span>
                          <span className="text-slate-400 text-xs truncate">{state.name}</span>
                        </div>
                        <FileText className="h-3 w-3 text-slate-600 group-hover:text-teal-400 flex-shrink-0 ml-1" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-slate-500">
            Requirements change frequently. Verify current rules with your state or county authority.
          </p>
        </div>
      </section>

      {/* CTA Section - Condensed */}
      <section className="py-12 relative">
        <div className="section-container">
          <div className="cta-card relative bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 rounded-2xl p-6 md:p-8 border border-slate-800 overflow-hidden">
            <div className="cta-glow absolute top-0 right-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl" />
            <div className="cta-glow absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Join Servio Today
              </h2>
              <p className="text-slate-400 text-sm max-w-lg mx-auto mb-6">
                Create your free profile and start receiving quote requests from clients nationwide.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild className="btn-accent px-6 py-5">
                  <Link to="/register">
                    Create Free Profile
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="btn-secondary px-6 py-5">
                  <Link to="/pricing#plans">View Pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ResourcesPage;
